import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Avatar, Col, Input, Row, Skeleton, Space, Typography } from 'antd';
import { kebabCase } from 'change-case';
import { TbAt, TbUser, TbUsers } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { ResourceDraft } from '@/hooks/useResourceDrafts';
import { useSubmitShortcut } from '@/hooks/useSubmitShortcut';
import { teamService } from '@/services';
import { StatusBadge } from '@/shared/components/badges';
import { FilterRecommendationButton } from '@/shared/components/filters';
import { PlatformForm as Form } from '@/shared/components/forms';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useActiveWorkspaceMembers, useMemberStore, usePresenceStore, useTeamStore } from '@/stores';
import { Member, SetStateMethod, Team, TeammatePermissionType, TeamVisibilityType } from '@/types';

const getMemberName = (member: Member) =>
    [member.user?.firstName, member.user?.lastName].filter(Boolean).join(' ') ||
    member.user?.emailAddress ||
    'Workspace Member';

interface CreateProps {
    open: boolean;
    setOpen: any;
    disabled?: boolean;
    initialDraft?: ResourceDraft<any> | null;
    onCreated?: () => void;
    onDraftValuesChange?: (values: {
        backgroundColor: string;
        description: string;
        emoji: string;
        handle: string;
        memberIds: string[];
        name: string;
        visibility: TeamVisibilityType;
    }) => void;
    createMore?: boolean;
    onFooterStateChange?: (state: { disabled: boolean; loading: boolean; onCreate: () => void }) => void;
    setVisibility: SetStateMethod<TeamVisibilityType>;
    visibility: TeamVisibilityType;
}

const Create: FC<CreateProps> = ({
    open,
    setOpen,
    disabled = false,
    initialDraft,
    onCreated,
    onDraftValuesChange,
    createMore,
    onFooterStateChange,
    setVisibility,
    visibility,
}): JSX.Element => {
    const [loading, setLoading] = useState(true);

    const { token } = useAntDesignTheme();

    const [emoji, setEmoji] = useState('');
    const [backgroundColor, setBackgroundColor] = useState('');
    const [name, setName] = useState('');
    const [handle, setHandle] = useState('');
    const [description, setDescription] = useState('');
    const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);

    const { fetchMembers, loading: membersLoading, selectedMember } = useMemberStore();
    const activeWorkspaceMembers = useActiveWorkspaceMembers();
    const presenceUsers = usePresenceStore((state) => state.users);

    useEffect(() => {
        setLoading(false);

        setBackgroundColor(token.colorBorder);
    }, [token.colorBorder]);

    useEffect(() => {
        if (open && initialDraft?.values) {
            setEmoji(initialDraft.values.emoji ?? '');
            setBackgroundColor(initialDraft.values.backgroundColor ?? token.colorBorder);
            setName(initialDraft.values.name ?? '');
            setHandle(initialDraft.values.handle ?? '');
            setVisibility(initialDraft.values.visibility ?? TeamVisibilityType.PRIVATE);
            setDescription(initialDraft.values.description ?? '');
            setSelectedMemberIds(initialDraft.values.memberIds ?? []);
        }
    }, [initialDraft, open, setVisibility, token.colorBorder]);

    useEffect(() => {
        if (open && activeWorkspaceMembers.length === 0 && !membersLoading) {
            void fetchMembers();
        }
    }, [activeWorkspaceMembers.length, fetchMembers, membersLoading, open]);

    const workspaceMembers = useMemo(
        () =>
            activeWorkspaceMembers
                .filter((member) => member.id !== selectedMember?.id)
                .sort((left, right) => getMemberName(left).localeCompare(getMemberName(right))),
        [activeWorkspaceMembers, selectedMember?.id],
    );

    useEffect(() => {
        onDraftValuesChange?.({
            backgroundColor,
            description,
            emoji,
            handle,
            memberIds: selectedMemberIds,
            name,
            visibility,
        });
    }, [backgroundColor, description, emoji, handle, name, onDraftValuesChange, selectedMemberIds, visibility]);

    const isFormDisabled = disabled || name === '';

    const { createTeam } = useTeamStore();

    const resetForm = useCallback(() => {
        setName('');
        setHandle('');
        setDescription('');
        setEmoji('');
        setSelectedMemberIds([]);
        setBackgroundColor(token.colorBorder);
        setVisibility(TeamVisibilityType.PRIVATE);
    }, [setVisibility, token.colorBorder]);

    const handleCreateTeam = useCallback(async () => {
        if (disabled) {
            return;
        }

        setLoading(true);

        const team = await createTeam({
            background: backgroundColor,
            description,
            emoji,
            handle: handle ? kebabCase(handle) : undefined,
            name,
            visibility,
        } as Partial<Team>);

        if (!team) {
            setLoading(false);
            toast.error('Unable to Create Team');

            return;
        }

        const selectedMembers = workspaceMembers.filter((member) => selectedMemberIds.includes(member.id));
        const invitationResults = await Promise.allSettled(
            selectedMembers.map((member) =>
                teamService.inviteWorkspaceMember(team.id, {
                    inviteeId: member.user.id,
                    permission: TeammatePermissionType.MEMBER,
                }),
            ),
        );

        if (invitationResults.some((result) => result.status === 'rejected')) {
            toast.warning('Team Created with Some Members Pending', {
                description: 'Some workspace members could not be added. You can invite them from the Team settings.',
            });
        }

        onCreated?.();
        resetForm();
        setLoading(false);

        if (!createMore) {
            setOpen(false);
        }
    }, [
        backgroundColor,
        createMore,
        createTeam,
        description,
        disabled,
        emoji,
        handle,
        name,
        onCreated,
        resetForm,
        selectedMemberIds,
        setOpen,
        visibility,
        workspaceMembers,
    ]);

    useEffect(() => {
        onFooterStateChange?.({
            disabled: isFormDisabled,
            loading,
            onCreate: handleCreateTeam,
        });
    }, [handleCreateTeam, isFormDisabled, loading, onFooterStateChange]);

    useSubmitShortcut(open && !disabled && !isFormDisabled && !loading, handleCreateTeam, [
        name,
        backgroundColor,
        createMore,
        disabled,
        emoji,
        visibility,
        handle,
        description,
        selectedMemberIds,
    ]);

    return (
        <Form layout='vertical' requiredMark={false} size='middle'>
            <Row align='bottom' gutter={[12, 0]}>
                <Col flex='none'>
                    <Form.Item style={{ marginBottom: '0.5rem' }}>
                        <Avatar
                            shape='square'
                            icon={<TbUsers style={{ color: token.colorTextTertiary }} />}
                            style={{
                                border: `1px solid ${token.colorBorder}`,
                            }}
                        />
                    </Form.Item>
                </Col>
                <Col flex='auto'>
                    <Form.Item style={{ marginBottom: '0.5rem' }} required label='Name'>
                        <Input
                            placeholder='Team A...'
                            value={name}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value)}
                        />
                    </Form.Item>
                </Col>
                <Col flex='auto'>
                    <Form.Item label='Handle (Optional)' style={{ marginBottom: '0.5rem' }}>
                        <Input
                            addonBefore={
                                <TbAt
                                    size='1rem'
                                    style={{
                                        color: token.colorTextTertiary,
                                        margin: '0 -0.25rem',
                                        verticalAlign: '-0.2rem',
                                    }}
                                />
                            }
                            placeholder='team-a...'
                            value={handle}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setHandle(event.target.value)}
                        />
                    </Form.Item>
                </Col>
            </Row>
            <Form.Item style={{ marginBottom: '0.5rem' }} label='Description'>
                <Input.TextArea
                    placeholder='About The Team...'
                    value={description}
                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(event.target.value)}
                    rows={4}
                    style={{ resize: 'none' }}
                />
            </Form.Item>
            <Form.Item label='Workspace Members (Optional)' style={{ marginBottom: 0 }}>
                <div
                    aria-label='Workspace members available to add'
                    style={{
                        border: `1px dashed ${token.colorBorder}`,
                        borderRadius: token.borderRadius,
                        maxHeight: '9rem',
                        minHeight: '4rem',
                        overflowY: 'auto',
                        padding: '0.75rem',
                    }}
                >
                    {membersLoading ? (
                        <Space size={4} wrap>
                            {Array.from({ length: 4 }, (_, index) => (
                                <Skeleton.Button active key={index} size='small' style={{ width: '8rem' }} />
                            ))}
                        </Space>
                    ) : workspaceMembers.length > 0 ? (
                        <Space size={[4, 4]} wrap>
                            {workspaceMembers.map((member) => {
                                const name = getMemberName(member);
                                const selected = selectedMemberIds.includes(member.id);
                                const presenceStatus = presenceUsers[member.user.id]?.status ?? 'offline';

                                return (
                                    <FilterRecommendationButton
                                        key={member.id}
                                        recommendation={{
                                            actionLabel: `${selected ? 'Remove' : 'Add'} ${name} ${
                                                selected ? 'from' : 'to'
                                            } team`,
                                            icon: (
                                                <StatusBadge
                                                    borderColor={
                                                        selected ? token.colorPrimaryBg : token.colorBgContainer
                                                    }
                                                    offset={[-3, 15]}
                                                    status={presenceStatus}
                                                >
                                                    <Avatar icon={<TbUser />} size={16} src={member.user.avatar} />
                                                </StatusBadge>
                                            ),
                                            key: member.id,
                                            label: name,
                                            onClick: () =>
                                                setSelectedMemberIds((current) =>
                                                    current.includes(member.id)
                                                        ? current.filter((memberId) => memberId !== member.id)
                                                        : [...current, member.id],
                                                ),
                                            selected,
                                        }}
                                    />
                                );
                            })}
                        </Space>
                    ) : (
                        <Typography.Text type='secondary'>No additional active workspace members.</Typography.Text>
                    )}
                </div>
            </Form.Item>
        </Form>
    );
};

export default Create;
